import { router } from "@inertiajs/react";
import { CalendarClock, LoaderIcon, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { formatCurrency } from "@/lib/utils";
import type { IncentiveRule } from "@/types/admin-portal/bonus-ro";
import {
	type AddablePackageGroup,
	dayAfter,
	formatDay,
	laterDay,
	parseAmount,
	sendWrite,
} from "./rules";

function reloadRules() {
	router.reload({ only: ["rules"] });
}

/** A whole-Rupiah input that echoes the amount it read; `hint` shows until it reads one. */
function RupiahField({
	id,
	label,
	value,
	onChange,
	placeholder,
	hint,
	disabled,
}: {
	id: string;
	label: string;
	value: string;
	onChange: (value: string) => void;
	placeholder: string;
	hint: string;
	disabled?: boolean;
}) {
	const parsed = parseAmount(value);
	const invalid = value !== "" && parsed === null;

	return (
		<div className="flex flex-col gap-2">
			<Label htmlFor={id}>{label}</Label>
			<Input
				className="bg-input shadow-inner"
				id={id}
				inputMode="numeric"
				value={value}
				placeholder={placeholder}
				disabled={disabled}
				aria-invalid={invalid}
				onChange={(e) => onChange(e.target.value)}
			/>
			<p
				className={
					invalid ? "text-xs text-destructive" : "text-xs text-muted-foreground"
				}
			>
				{parsed === null ? hint : formatCurrency(parsed)}
			</p>
		</div>
	);
}

function AmountAndStartFields({
	amountLabel,
	amount,
	onAmountChange,
	startsOn,
	onStartsOnChange,
	minStartsOn,
	disabled,
}: {
	amountLabel: string;
	amount: string;
	onAmountChange: (value: string) => void;
	startsOn: string;
	onStartsOnChange: (value: string) => void;
	minStartsOn: string;
	disabled?: boolean;
}) {
	const { t } = useTranslation("bonus-ro");

	return (
		<div className="grid gap-4 sm:grid-cols-2">
			<RupiahField
				id="bonus-ro-amount"
				label={amountLabel}
				value={amount}
				onChange={onAmountChange}
				placeholder="85000"
				hint={t("fields.amount_hint")}
				disabled={disabled}
			/>
			<div className="flex flex-col gap-2">
				<Label htmlFor="bonus-ro-starts-on">{t("fields.starts_on")}</Label>
				<Input
					className="bg-input shadow-inner"
					id="bonus-ro-starts-on"
					type="date"
					min={minStartsOn}
					value={startsOn}
					disabled={disabled}
					onChange={(e) => onStartsOnChange(e.target.value)}
				/>
				<p className="text-xs text-muted-foreground">
					{t("fields.starts_on_hint", { date: formatDay(minStartsOn) })}
				</p>
			</div>
		</div>
	);
}

/** The patient's discount an RKI voucher gives on the package; `keepHint` makes it optional. */
function CustomerDiscountField({
	value,
	onChange,
	keepHint,
	disabled,
}: {
	value: string;
	onChange: (value: string) => void;
	keepHint?: string;
	disabled?: boolean;
}) {
	const { t } = useTranslation("bonus-ro");
	return (
		<RupiahField
			id="bonus-ro-customer-discount"
			label={t("fields.customer_discount")}
			value={value}
			onChange={onChange}
			placeholder="50000"
			hint={
				value === "" && keepHint ? keepHint : t("fields.customer_discount_hint")
			}
			disabled={disabled}
		/>
	);
}

/** A start day of today reaches back to the orders already created today. */
function TodayEffect({
	i18nKey,
	values,
}: {
	i18nKey: string;
	values: Record<string, string>;
}) {
	const { t } = useTranslation("bonus-ro");
	return (
		<Alert className="border-amber-500 bg-amber-50/60 text-amber-700 dark:bg-amber-950/20 dark:text-amber-400">
			<CalendarClock className="size-4" />
			<AlertTitle>{t("today_effect.title")}</AlertTitle>
			<AlertDescription>
				<Trans
					i18nKey={i18nKey}
					ns="bonus-ro"
					values={values}
					components={{
						b: (
							<strong className="font-semibold tabular-nums text-foreground" />
						),
					}}
				/>
			</AlertDescription>
		</Alert>
	);
}

/**
 * Re-prices a package's last rule. The server closes it the day before the start day,
 * then creates the new rule; when only the create fails, the dialog stays open on that
 * state and offers to retry the create alone with the same values.
 */
export function ChangeAmountDialog({
	rule,
	amountUrl,
	createUrl,
	today,
	onClose,
}: {
	rule: IncentiveRule;
	/** contains ":id" */
	amountUrl: string;
	createUrl: string;
	today: string;
	onClose: () => void;
}) {
	const { t } = useTranslation("bonus-ro");
	// A rule scheduled ahead can only be closed on or after its own first day.
	const minStartsOn = laterDay(today, dayAfter(rule.validFrom));
	const [amount, setAmount] = useState("");
	const [discount, setDiscount] = useState("");
	const [startsOn, setStartsOn] = useState(minStartsOn);
	const [saving, setSaving] = useState(false);
	const [partial, setPartial] = useState<{
		closedUntil: string;
		error: string;
	} | null>(null);

	const amountValue = parseAmount(amount);
	// Left empty it is not sent, and the backend carries the current discount over. With
	// none to carry over the package would refuse every RKI code, so it is required then.
	const discountValue = parseAmount(discount) ?? undefined;
	const discountOptional = rule.customerDiscountAmount !== null;
	const canSave =
		amountValue !== null &&
		(discountValue !== undefined || (discountOptional && discount === "")) &&
		startsOn >= minStartsOn &&
		!saving &&
		!partial;

	function done() {
		toast.success(t("messages.amount_changed"));
		onClose();
		reloadRules();
	}

	async function onSave() {
		setSaving(true);
		const result = await sendWrite(
			amountUrl.replace(":id", String(rule.id)),
			"PUT",
			{
				amount: amountValue,
				starts_on: startsOn,
				customer_discount_amount: discountValue,
			},
			t("messages.error"),
		);
		setSaving(false);
		if (result.ok) return done();
		if (result.ruleClosedUntil) {
			setPartial({ closedUntil: result.ruleClosedUntil, error: result.error });
			reloadRules();
			return;
		}
		toast.error(result.error);
	}

	async function onRetryCreate() {
		if (!partial) return;
		setSaving(true);
		const result = await sendWrite(
			createUrl,
			"POST",
			{
				package_id: rule.packageId,
				amount: amountValue,
				valid_from: startsOn,
				customer_discount_amount: discountValue,
			},
			t("messages.error"),
		);
		setSaving(false);
		if (result.ok) return done();
		setPartial({ ...partial, error: result.error });
	}

	return (
		<Dialog open onOpenChange={(open) => !open && !saving && onClose()}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>
						{t("change.title", { package: rule.packageName })}
					</DialogTitle>
					<DialogDescription>{t("change.description")}</DialogDescription>
				</DialogHeader>

				<dl className="grid grid-cols-2 gap-px border bg-border sm:grid-cols-3">
					<div className="flex flex-col gap-0.5 bg-background px-3 py-2">
						<dt className="text-xs text-muted-foreground">
							{t("change.current_amount")}
						</dt>
						<dd className="text-sm font-semibold tabular-nums">
							{formatCurrency(rule.amount)}
						</dd>
					</div>
					<div className="flex flex-col gap-0.5 bg-background px-3 py-2">
						<dt className="text-xs text-muted-foreground">
							{t("change.current_period")}
						</dt>
						<dd className="text-sm font-semibold">
							{t("change.since", { date: formatDay(rule.validFrom) })}
						</dd>
					</div>
					<div className="flex flex-col gap-0.5 bg-background px-3 py-2">
						<dt className="text-xs text-muted-foreground">
							{t("change.current_discount")}
						</dt>
						<dd className="text-sm font-semibold tabular-nums">
							{rule.customerDiscountAmount === null
								? t("change.no_discount")
								: formatCurrency(rule.customerDiscountAmount)}
						</dd>
					</div>
				</dl>

				<AmountAndStartFields
					amountLabel={t("fields.new_amount")}
					amount={amount}
					onAmountChange={setAmount}
					startsOn={startsOn}
					onStartsOnChange={setStartsOn}
					minStartsOn={minStartsOn}
					disabled={!!partial}
				/>

				<CustomerDiscountField
					value={discount}
					onChange={setDiscount}
					keepHint={
						rule.customerDiscountAmount === null
							? undefined
							: t("fields.customer_discount_keep", {
									amount: formatCurrency(rule.customerDiscountAmount),
								})
					}
					disabled={!!partial}
				/>

				{startsOn === today && !partial && (
					<TodayEffect
						i18nKey={
							amountValue === null
								? "today_effect.change_no_amount"
								: "today_effect.change"
						}
						values={{
							from: formatCurrency(rule.amount),
							to: amountValue === null ? "" : formatCurrency(amountValue),
						}}
					/>
				)}

				{partial && (
					<Alert variant="destructive">
						<TriangleAlert className="size-4" />
						<AlertTitle>
							{t("change.partial_title", {
								date: formatDay(partial.closedUntil),
							})}
						</AlertTitle>
						<AlertDescription>
							{t("change.partial_body", { error: partial.error })}
						</AlertDescription>
					</Alert>
				)}

				<DialogFooter>
					<Button variant="outline" onClick={onClose} disabled={saving}>
						{partial ? t("actions.close") : t("actions.cancel")}
					</Button>
					{partial ? (
						<Button onClick={onRetryCreate} disabled={saving}>
							{saving && <LoaderIcon className="size-4 animate-spin" />}
							{t("change.retry")}
						</Button>
					) : (
						<Button onClick={onSave} disabled={!canSave}>
							{saving && <LoaderIcon className="size-4 animate-spin" />}
							{t("actions.save")}
						</Button>
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

export function AddRuleDialog({
	options,
	createUrl,
	today,
	onClose,
}: {
	options: AddablePackageGroup[];
	createUrl: string;
	today: string;
	onClose: () => void;
}) {
	const { t } = useTranslation("bonus-ro");
	const [packageId, setPackageId] = useState("");
	const [amount, setAmount] = useState("");
	const [discount, setDiscount] = useState("");
	const [startsOn, setStartsOn] = useState(today);
	const [saving, setSaving] = useState(false);

	const packages = options.flatMap((group) => group.packages);
	const minStartsOn =
		packages.find((p) => String(p.id) === packageId)?.minStartsOn ?? today;
	const amountValue = parseAmount(amount);
	const discountValue = parseAmount(discount);
	const canSave =
		packageId !== "" &&
		amountValue !== null &&
		discountValue !== null &&
		startsOn >= minStartsOn &&
		!saving;

	function onPackageChange(value: string) {
		setPackageId(value);
		const min = packages.find((p) => String(p.id) === value)?.minStartsOn;
		if (min) setStartsOn((day) => laterDay(day, min));
	}

	async function onSave() {
		setSaving(true);
		const result = await sendWrite(
			createUrl,
			"POST",
			{
				package_id: Number(packageId),
				amount: amountValue,
				valid_from: startsOn,
				customer_discount_amount: discountValue,
			},
			t("messages.error"),
		);
		setSaving(false);
		if (!result.ok) {
			toast.error(result.error);
			return;
		}
		toast.success(t("messages.rule_created"));
		onClose();
		reloadRules();
	}

	return (
		<Dialog open onOpenChange={(open) => !open && !saving && onClose()}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{t("add.title")}</DialogTitle>
					<DialogDescription>{t("add.description")}</DialogDescription>
				</DialogHeader>

				<div className="flex flex-col gap-2">
					<Label>{t("fields.package")}</Label>
					<Select value={packageId} onValueChange={onPackageChange}>
						<SelectTrigger className="bg-input shadow-inner">
							<SelectValue placeholder={t("fields.package_placeholder")} />
						</SelectTrigger>
						<SelectContent>
							{options.map((group) => (
								<SelectGroup key={group.serviceName}>
									<SelectLabel>
										{group.serviceName.replaceAll("_", " ")}
									</SelectLabel>
									{group.packages.map((p) => (
										<SelectItem key={p.id} value={String(p.id)}>
											{p.name}
										</SelectItem>
									))}
								</SelectGroup>
							))}
						</SelectContent>
					</Select>
				</div>

				<AmountAndStartFields
					amountLabel={t("fields.amount")}
					amount={amount}
					onAmountChange={setAmount}
					startsOn={startsOn}
					onStartsOnChange={setStartsOn}
					minStartsOn={minStartsOn}
				/>

				<CustomerDiscountField value={discount} onChange={setDiscount} />

				{startsOn === today && (
					<TodayEffect
						i18nKey={
							amountValue === null
								? "today_effect.add_no_amount"
								: "today_effect.add"
						}
						values={{
							amount: amountValue === null ? "" : formatCurrency(amountValue),
						}}
					/>
				)}

				<DialogFooter>
					<Button variant="outline" onClick={onClose} disabled={saving}>
						{t("actions.cancel")}
					</Button>
					<Button onClick={onSave} disabled={!canSave}>
						{saving && <LoaderIcon className="size-4 animate-spin" />}
						{t("actions.save")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
